import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import './worki.css';
import { IDLE, Pose } from './Pose.tsx';

const BASE_SIZE = 280;

interface WorkiProps {
    pose?: Pose;
    size?: number;
    trackMouse?: 'element' | 'page' | 'none';
    showGlow?: boolean;
    idle?: boolean;
}

export interface WorkiHandle {
    burst: (clientX?: number, clientY?: number) => void;
}

const Worki = forwardRef<WorkiHandle, WorkiProps>(function Worki(
    { pose = IDLE, size = BASE_SIZE, trackMouse = 'page', showGlow = true, idle = true },
    ref
) {
    const scale = size / BASE_SIZE;
    const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
    const [blink, setBlink] = useState(false);
    const [wiggle, setWiggle] = useState(0);
    const [particles, setParticles] = useState<
        { id: number; x: number; y: number; dx: number; dy: number; size: number; hue: number }[]
    >([]);
    const [bodyScale, setBodyScale] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const idRef = useRef(0);

    const [outgoingEyes, setOutgoingEyes] = useState<{ pose: Pose; fadingOut: boolean } | null>(null);
    const prevEyesRef = useRef({ left: pose.leftEye, right: pose.rightEye, pose });
    useEffect(() => {
        const prev = prevEyesRef.current;
        if (prev.left === pose.leftEye && prev.right === pose.rightEye) return;
        const outgoingPose = prev.pose;
        prevEyesRef.current = { left: pose.leftEye, right: pose.rightEye, pose };
        setOutgoingEyes({ pose: outgoingPose, fadingOut: false });
        const startFade = requestAnimationFrame(() =>
            requestAnimationFrame(() => setOutgoingEyes((current) => current && { ...current, fadingOut: true }))
        );
        const clear = setTimeout(() => setOutgoingEyes(null), 400);
        return () => {
            cancelAnimationFrame(startFade);
            clearTimeout(clear);
        };
    }, [pose]);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (trackMouse !== 'element' || !containerRef.current) return;
            const r = containerRef.current.getBoundingClientRect();
            setMouse({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
        },
        [trackMouse]
    );

    useEffect(() => {
        if (trackMouse !== 'page') return;
        const handleGlobalMove = (e: MouseEvent) => {
            if (!containerRef.current) return;
            const r = containerRef.current.getBoundingClientRect();
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            setMouse({
                x: Math.max(0, Math.min(1, 0.5 + (e.clientX - cx) / window.innerWidth)),
                y: Math.max(0, Math.min(1, 0.5 + (e.clientY - cy) / window.innerHeight)),
            });
        };
        window.addEventListener('mousemove', handleGlobalMove);
        return () => window.removeEventListener('mousemove', handleGlobalMove);
    }, [trackMouse]);

    useEffect(() => {
        const interval = setInterval(
            () => {
                setBlink(true);
                setTimeout(() => setBlink(false), 150);
            },
            2500 + Math.random() * 2000
        );
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!idle) return;
        let frame: number,
            t = 0;
        const animate = () => {
            t += 0.02;
            setWiggle(t);
            frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [idle]);

    const burst = (clientX?: number, clientY?: number) => {
        setBodyScale(0.92);
        setTimeout(() => setBodyScale(1), 150);
        const r = containerRef.current!.getBoundingClientRect();
        const targetX = clientX ?? r.left + r.width / 2;
        const targetY = clientY ?? r.top + r.height * 0.05;
        const px = (targetX - r.left) / scale,
            py = (targetY - r.top) / scale;
        const newP = Array.from({ length: 6 }, () => ({
            id: idRef.current++,
            x: px,
            y: py,
            dx: (Math.random() - 0.5) * 120,
            dy: -Math.random() * 80 - 20,
            size: 4 + Math.random() * 8,
            hue: 245 + Math.random() * 20,
        }));
        setParticles((p) => [...p, ...newP]);
        setTimeout(() => setParticles((p) => p.filter((pp) => !newP.includes(pp))), 800);
    };

    useImperativeHandle(ref, () => ({ burst }));

    const handleMascotClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        burst(e.clientX, e.clientY);
    };

    const floatY = idle ? Math.sin(wiggle) * 8 : 0;
    const floatRot = idle ? Math.sin(wiggle * 0.7) * 2.5 : 0;
    const limbBob = (offset: number) => (idle ? Math.sin(wiggle * 1.2 + offset) * 4 : 0);

    const ep = pose.eyeProps;
    const eyeOffX = (mouse.x - 0.5) * 8 + (ep.offX || 0);
    const eyeOffY = (mouse.y - 0.5) * 5 + (ep.offY || 0);

    const LeftEyeComp = pose.leftEye;
    const RightEyeComp = pose.rightEye;
    const MouthComp = pose.mouth || null;
    const LeftHandComp = pose.leftHand;
    const RightHandComp = pose.rightHand;
    const BodyComp = pose.body;

    return (
        <div
            style={{
                width: size,
                height: size,
                overflow: 'visible',
            }}
        >
            <div
                ref={containerRef}
                onMouseMove={handleMouseMove}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    userSelect: 'none',
                    transform: `scale(${scale})`,
                    transformOrigin: 'top center',
                }}
            >
                {particles.map((p) => (
                    <div
                        key={p.id}
                        style={
                            {
                                position: 'absolute',
                                left: p.x,
                                top: p.y,
                                width: p.size,
                                height: p.size,
                                borderRadius: '50%',
                                background: `hsla(${p.hue}, 75%, 55%, 0.9)`,
                                animation: 'particleFly 0.8s ease-out forwards',
                                '--dx': `${p.dx}px`,
                                '--dy': `${p.dy}px`,
                                pointerEvents: 'none',
                                zIndex: 100,
                            } as React.CSSProperties
                        }
                    />
                ))}

                <div
                    onClick={handleMascotClick}
                    style={
                        {
                            position: 'relative',
                            width: BASE_SIZE,
                            height: BASE_SIZE,
                            cursor: 'pointer',
                            transform: `translateY(${floatY}px) rotate(${floatRot}deg) scale(${bodyScale})`,
                            transition: 'transform 0.15s ease-out',
                            '--mouse-x': mouse.x - 0.5,
                            '--mouse-y': mouse.y - 0.5,
                        } as React.CSSProperties
                    }
                >
                    {showGlow && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: -40,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, #8b8bf540 0%, transparent 70%)',
                                filter: 'blur(20px)',
                            }}
                        />
                    )}

                    <LeftHandComp
                        {...pose.leftHandProps}
                        bob={limbBob(0)}
                        animate={pose.leftHandProps.animate}
                        animName={pose.leftHandProps.animate || ''}
                    />
                    <RightHandComp
                        {...pose.rightHandProps}
                        bob={limbBob(1)}
                        animate={pose.rightHandProps.animate}
                        animName={pose.rightHandProps.animate || ''}
                    />

                    <BodyComp />

                    <div
                        style={{
                            position: 'absolute',
                            top: '38%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            gap: ep.gap,
                            zIndex: 2,
                            transition: 'gap 0.3s',
                        }}
                    >
                        <LeftEyeComp w={ep.w} h={ep.h} offX={eyeOffX} offY={eyeOffY} blink={blink} />
                        <RightEyeComp w={ep.w} h={ep.h} offX={eyeOffX} offY={eyeOffY} blink={blink} />
                    </div>

                    {outgoingEyes && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '38%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                display: 'flex',
                                gap: outgoingEyes.pose.eyeProps.gap,
                                zIndex: 3,
                                opacity: outgoingEyes.fadingOut ? 0 : 1,
                                transition: 'opacity 400ms',
                                pointerEvents: 'none',
                            }}
                        >
                            <outgoingEyes.pose.leftEye
                                w={outgoingEyes.pose.eyeProps.w}
                                h={outgoingEyes.pose.eyeProps.h}
                                offX={eyeOffX}
                                offY={eyeOffY}
                                blink={blink}
                            />
                            <outgoingEyes.pose.rightEye
                                w={outgoingEyes.pose.eyeProps.w}
                                h={outgoingEyes.pose.eyeProps.h}
                                offX={eyeOffX}
                                offY={eyeOffY}
                                blink={blink}
                            />
                        </div>
                    )}

                    {MouthComp && <MouthComp />}

                    {(pose.accessories || []).map((Acc, i) => (
                        <Acc key={i} />
                    ))}
                </div>
            </div>
        </div>
    );
});

export default Worki;
